import {
    CORE_8_PORTFOLIO,
    type HouseholdInputs,
    MarginalTaxRateCalculator,
    type MemberInput,
    PortfolioCalculator,
    type RetirementAccount,
    RetirementAccountType,
    type RetirementCalculatorInputs,
    RetirementContributionWaterfallGenerator,
    SocialSecurityBenefitCalculator,
} from '@fruitful/financial-calculators';
import { roundToTwoDecimals } from '@fruitful/fruitils';
import type { MembershipIncome } from '@fruitful/member-profile';
import {
    AccountCategory,
    type AccountSummary,
    type HolisticSummary,
} from '@fruitful/types';
import differenceInYears from 'date-fns/differenceInYears';
import parseISO from 'date-fns/parseISO';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

import { selectedMembershipAtom } from '../../entities/entityAtoms';
import {
    type Bill,
    EmploymentStatus,
    type MemberDetails,
    type MembershipDetails,
    UserType,
} from '../../entities/entityTypes';
import { useMembershipBills } from '../../entities/membership/hooks/useMembershipBills';
import { useMembershipHolisticSummary } from '../../entities/membership/hooks/useMembershipHolisticSummary';

// Constants for retirement calculations
const DEFAULT_TARGET_SUCCESS_LIKELIHOOD = 0.8;
const DEFAULT_RETIREMENT_AGE = 67;
const DEFAULT_SOCIAL_SECURITY_BENEFIT_AGE = 67;
const DEFAULT_LIFE_EXPECTANCY = 90;
const DEFAULT_EMPLOYER_MATCH_MAXIMUM_PERCENT = 0.03;
const DEFAULT_EMPLOYER_MATCH_PERCENTAGE = 1.0;
const DEFAULT_RETIREMENT_HEALTHCARE_COSTS = 6400; // Copied from Right Capital. Applied to every year in retirement.
const DEFAULT_RETIREMENT_LTC_COSTS = 75000; // Copied from Right Capital. Applied only to last 2 years of life.
const MONTHS_PER_YEAR = 12;
const WEEKS_PER_YEAR = 52;
const CENTS_PER_DOLLAR = 100;

const getDefaultEquitiesPercentage = (): number => {
    const allocations =
        PortfolioCalculator.calculateAllocations(CORE_8_PORTFOLIO);
    return allocations.equities;
};

/**
 * Hook that gathers all available data for retirement calculator inputs
 */
export const useRetirementCalculatorInputs = (): {
    retirementCalculatorInputs: RetirementCalculatorInputs;
    isLoading: boolean;
} => {
    const membershipDetails = useAtomValue(selectedMembershipAtom);
    const { membershipHolisticSummary, isLoadingHolisticSummary } =
        useMembershipHolisticSummary(membershipDetails?.id ?? null);
    const { membershipBills, isLoadingBills } = useMembershipBills(
        membershipDetails?.id ?? null,
    );

    const retirementCalculatorInputs =
        useMemo((): RetirementCalculatorInputs => {
            if (!membershipDetails) {
                throw new Error('No membership details found.');
            }

            const { users, membershipIncomes } = membershipDetails;

            // Find primary and partner members
            const primaryMember = users.find(
                (m) => m.type === UserType.PRIMARY_MEMBER,
            );
            const partnerMember = users.find(
                (m) => m.type === UserType.PARTNER_MEMBER,
            );

            if (!primaryMember) {
                throw new Error('No primary member found for membership.');
            }

            // Calculate member inputs
            const memberInputs = calculateMemberInputs(
                primaryMember,
                partnerMember,
                membershipIncomes,
                membershipHolisticSummary?.holisticSummary,
            );

            // Calculate household inputs
            const householdInputs = calculateHouseholdInputs(
                membershipDetails,
                membershipIncomes,
                membershipBills?.bills ?? [],
                primaryMember,
                memberInputs,
            );

            return {
                householdInputs,
                memberInputs,
            };
        }, [membershipDetails, membershipHolisticSummary, membershipBills]);

    return {
        retirementCalculatorInputs,
        isLoading: isLoadingHolisticSummary || isLoadingBills,
    };
};

const calculateHouseholdInputs = (
    membershipDetails: MembershipDetails,
    membershipIncomes: MembershipIncome[],
    membershipBills: Bill[],
    primaryMember: MemberDetails,
    memberInputs: MemberInput[],
): HouseholdInputs => {
    // Calculate annual spending in retirement based on current expenses
    const monthlyBillsCents = (membershipBills ?? []).reduce(
        (total, { monthlyAmount }) => total + monthlyAmount,
        0,
    );
    const monthlyBills = roundToTwoDecimals(
        monthlyBillsCents / CENTS_PER_DOLLAR,
    ); // monthly amount in dollars
    const monthlySpend = membershipDetails.membershipSpend?.amount ?? 0; // monthly amount in dollars.

    // Estimate annual spending in retirement (current bills + current spend)
    const annualSpendingInRetirement = roundToTwoDecimals(
        (monthlyBills + monthlySpend) * MONTHS_PER_YEAR,
    );

    const taxFilingStatus = primaryMember.taxFiling;
    if (taxFilingStatus == null) {
        throw new Error(
            'Member must have a Tax Filing listed in their Member Profile.',
        );
    }

    const residentialLocation = primaryMember.userAddress?.state ?? 'US';

    const grossAnnualIncome = calculateTotalAnnualIncome(membershipIncomes);

    // Estimate current marginal tax rate based on income
    const taxRateCalculator = new MarginalTaxRateCalculator(
        grossAnnualIncome,
        residentialLocation,
        taxFilingStatus,
    );
    const currentMarginalTaxRate = taxRateCalculator.calculate();

    const targetSuccessLikelihood = DEFAULT_TARGET_SUCCESS_LIKELIHOOD;

    const retirementContributionWaterfallGenerator =
        new RetirementContributionWaterfallGenerator(
            currentMarginalTaxRate,
            memberInputs,
        );
    const retirementContributionWaterfall =
        retirementContributionWaterfallGenerator.generate();

    return {
        annualSpendingInRetirement,
        targetSuccessLikelihood,
        taxFilingStatus,
        residentialLocation,
        currentMarginalTaxRate,
        retirementContributionWaterfall,
    };
};

/**
 * Calculate member-level inputs for retirement calculator
 */
const calculateMemberInputs = (
    primaryMember: MemberDetails,
    partnerMember: MemberDetails | undefined,
    membershipIncomes: MembershipIncome[],
    holisticSummary: HolisticSummary | undefined,
): MemberInput[] => {
    const members = [primaryMember];
    if (partnerMember) {
        members.push(partnerMember);
    }

    return members.map((member) => {
        const { id, dateOfBirth } = member;

        // Calculate current age from date of birth
        if (dateOfBirth == null) {
            throw new Error(
                'Members must have Date of Birth in their Member Profile.',
            );
        }
        const currentAge = calculateAge(dateOfBirth);
        const retirementAge = DEFAULT_RETIREMENT_AGE;
        const socialSecurityBenefitAge = DEFAULT_SOCIAL_SECURITY_BENEFIT_AGE;
        const lifeExpectancy = DEFAULT_LIFE_EXPECTANCY;

        // Find income for this member
        const memberIncome = membershipIncomes.find(
            (income) => income.belongsToUserId === member.id,
        );

        // Calculate current gross annual income
        const currentGrossAnnualIncome = memberIncome
            ? calculateTotalAnnualIncome([memberIncome])
            : 0;

        // Get retirement accounts from holistic summary
        const accounts = getRetirementAccounts(member.id, holisticSummary);

        // Determine if member has employer retirement plan
        const hasEmployerRetirementPlan =
            member.employmentStatus === EmploymentStatus.FULL_TIME;

        // These fields don't exist in the current data model
        const employerMatchMaximumPercentOfIncome =
            DEFAULT_EMPLOYER_MATCH_MAXIMUM_PERCENT;
        const employerMatchPercentage = DEFAULT_EMPLOYER_MATCH_PERCENTAGE;

        // Determine if member is self-employed
        const isSelfEmployed =
            member.employmentStatus === EmploymentStatus.FREELANCER ||
            member.employmentStatus === EmploymentStatus.BUSINESS_OWNER;

        const socialSecurityBenefitCalculator =
            new SocialSecurityBenefitCalculator(
                currentAge,
                socialSecurityBenefitAge,
                currentGrossAnnualIncome,
            );
        const annualSocialSecurityBenefit =
            socialSecurityBenefitCalculator.calculate();

        const longTermCareCost = DEFAULT_RETIREMENT_LTC_COSTS;
        const retirementHealthcareCost = DEFAULT_RETIREMENT_HEALTHCARE_COSTS;

        return {
            id,
            currentAge,
            retirementAge,
            socialSecurityBenefitAge,
            lifeExpectancy,
            currentGrossAnnualIncome,
            currentRetirementSavings: accounts,
            hasEmployerRetirementPlan,
            employerMatchMaximumPercentOfIncome,
            employerMatchPercentage,
            isSelfEmployed,
            annualSocialSecurityBenefit,
            includeRetirementHealthcareCosts: true,
            longTermCareCost,
            retirementHealthcareCost,
        };
    });
};

/**
 * Calculate age from date of birth
 */
const calculateAge = (dateOfBirth: string): number => {
    const birthDateWithoutTimeZone = dateOfBirth.split('T')[0] ?? dateOfBirth;
    const birthDate = parseISO(birthDateWithoutTimeZone);
    const today = new Date();
    return differenceInYears(today, birthDate);
};

/**
 * Extract retirement accounts from holistic summary
 */
const getRetirementAccounts = (
    userId: string,
    holisticSummary: HolisticSummary | undefined,
) => {
    if (!holisticSummary) {
        return [];
    }

    const accounts: RetirementAccount[] = [];

    // Look for investment accounts (IRAs, 401ks, etc.)
    const investAccounts =
        holisticSummary[AccountCategory.INVEST]?.accounts ?? [];
    for (const account of investAccounts) {
        if (account.userId === userId && isRetirementAccount(account)) {
            accounts.push({
                id: account.id,
                owner: userId,
                type: mapAccountTypeToRetirementType(account),
                balance: (account.currentBalanceCents ?? 0) / CENTS_PER_DOLLAR,
                equitiesPercentage: getDefaultEquitiesPercentage(),
                costBasis: 0, // Default cost basis to 0 for imported accounts
            });
        }
    }

    return accounts;
};

/**
 * Check if an account is a retirement account
 */
const isRetirementAccount = (account: AccountSummary): boolean => {
    const accountType = account.type?.toLowerCase() ?? '';
    const accountName = account.name?.toLowerCase() ?? '';

    return (
        accountType.includes('ira') ||
        accountType.includes('401k') ||
        accountType.includes('403b') ||
        accountType.includes('457') ||
        accountType.includes('roth') ||
        accountType.includes('sep') ||
        accountType.includes('simple') ||
        accountType.includes('pension') ||
        accountName.includes('ira') ||
        accountName.includes('401k') ||
        accountName.includes('403b') ||
        accountName.includes('457') ||
        accountName.includes('roth') ||
        accountName.includes('sep') ||
        accountName.includes('simple') ||
        accountName.includes('pension')
    );
};

/**
 * Map account type to retirement account type
 */
const mapAccountTypeToRetirementType = (
    account: AccountSummary,
): RetirementAccountType => {
    const accountType = account.type?.toLowerCase() ?? '';
    const accountName = account.name?.toLowerCase() ?? '';

    if (accountType.includes('roth') || accountName.includes('roth')) {
        if (accountType.includes('401k') || accountName.includes('401k')) {
            return RetirementAccountType.ROTH_401K;
        }
        return RetirementAccountType.ROTH_IRA;
    }
    if (accountType.includes('401k') || accountName.includes('401k')) {
        return RetirementAccountType.EMPLOYER_401K;
    }
    if (accountType.includes('ira') || accountName.includes('ira')) {
        return RetirementAccountType.TRAD_IRA;
    }
    if (accountType.includes('sep') || accountName.includes('sep')) {
        return RetirementAccountType.SEP_IRA;
    }
    if (accountType.includes('solo') || accountName.includes('solo')) {
        return RetirementAccountType.SOLO_401K;
    }

    // Default to taxable brokerage if we can't determine
    return RetirementAccountType.TAXABLE_BROKERAGE;
};

/**
 * Calculate total annual income from membership incomes
 */
const calculateTotalAnnualIncome = (
    membershipIncomes: MembershipIncome[],
): number => {
    return roundToTwoDecimals(
        (membershipIncomes ?? []).reduce((total, income) => {
            if (income.annualSalaryCents != null) {
                return total + income.annualSalaryCents / CENTS_PER_DOLLAR;
            }
            if (income.payPerHourCents != null && income.weeklyHours != null) {
                return (
                    total +
                    (income.payPerHourCents *
                        income.weeklyHours *
                        WEEKS_PER_YEAR) /
                        CENTS_PER_DOLLAR
                );
            }
            return total;
        }, 0),
    );
};
